import { styled } from '@mui/material';
import { PageContext } from 'pages/ForgotPassword/PageProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Form, Link, PasswordInput, Stack, Text, TextInput } from 'shell/components';

const NewPassword = ({
    device,
    errors,
    form,
    satisfyRequirement,
    handleChange,
    handleUpdatePassword,
    handleResendCode,
}) => {
    const { loading } = useContext(PageContext);
    const { t } = useTranslation('auth');

    return (
        <VerifyEmailBox device={device}>
            <Stack alignItems={'center'}>
                <Text component={'h1'} variant={'h6'}>
                    {t('forgot-password.title', 'Forgot your password?')}
                </Text>
                <Text variant={'p1'} align={'center'}>
                    A password reset code has been sent to <strong>{form.email}</strong>. Enter below to reset your
                    password:
                </Text>
            </Stack>
            <Form onSubmit={() => handleUpdatePassword()}>
                <InputStack spacing={6}>
                    <TextInput
                        autoComplete={'one-time-code'}
                        error={Boolean(errors.code)}
                        helperText={errors.code}
                        label={'Verification Code'}
                        value={form.code}
                        onChange={(e) => handleChange('code', e.target.value)}
                        placeholder={'Enter verification code'}
                        required
                    />
                    <PasswordInput
                        data-cy={'forgot-password-new-input'}
                        error={Boolean(errors.password)}
                        helperText={errors.password}
                        label={t('common:account-settings.password.enter-new-password', 'Enter New Password')}
                        value={form.password}
                        onChange={(e) => handleChange('password', e.target.value)}
                        placeholder={t('common:account-settings.password.enter-new-password', 'Enter new password')}
                        autoComplete={'new-password'}
                        required
                    />

                    <Stack sx={{ margin: (theme) => theme.spacing(2, 0, 6, 0) }}>
                        <Text variant={'p3'}>
                            {t('common:account-settings.password.password-must-have', 'The password must have')}:
                        </Text>
                        <Text variant={'p3'} color={satisfyRequirement.validateMoreThan8Characters ? 'error' : 'light'}>
                            • {t('common:account-settings.password.req-one-min-characters', 'At least 8 characters')}
                        </Text>
                        <Text variant={'p3'} color={satisfyRequirement.validateOneLowerCase ? 'error' : 'light'}>
                            •{' '}
                            {t('common:account-settings.password.req-three-lowercase', 'At least one lowercase letter')}
                        </Text>
                        <Text variant={'p3'} color={satisfyRequirement.validateOneUpperCase ? 'error' : 'light'}>
                            • {t('common:account-settings.password.req-two-uppercase', 'At least one uppercase letter')}
                        </Text>
                        <Text variant={'p3'} color={satisfyRequirement.validateOneNumber ? 'error' : 'light'}>
                            • {t('common:account-settings.password.req-four-number', 'At least one number')}
                        </Text>
                        <Text variant={'p3'} color={satisfyRequirement.validateOneSpecial ? 'error' : 'light'}>
                            • {t('common:account-settings.password.req-five-symbol', 'At least one special character')}
                        </Text>
                    </Stack>
                    <PasswordInput
                        data-cy='forgot-password-confirm-input'
                        error={Boolean(errors.confirmPassword)}
                        helperText={errors.confirmPassword}
                        label={t('common:account-settings.password.confirm-password', 'Confirm Password')}
                        value={form.confirmPassword}
                        onChange={(e) => handleChange('confirm-password', e.target.value)}
                        placeholder={t('common:account-settings.password.enter-new-password', 'Enter new password')}
                        autoComplete={'new-password'}
                        required
                    />
                </InputStack>
                <Stack sx={{ margin: (theme) => theme.spacing(6, 0, 3, 0) }}>
                    <Button data-cy={'forgot-password-change-password-button'} loading={loading} type={'submit'}>
                        {t('common:account-settings.password.title', 'Change Password')}
                    </Button>
                </Stack>
                <Stack alignItems={'center'} spacing={1}>
                    <Text>Didn’t receive the email?</Text>
                    <Button variant={'borderless'} size={'small'} onClick={handleResendCode}>
                        Resend email
                    </Button>
                    <Link to={'/'}>Back to login</Link>
                </Stack>
            </Form>
        </VerifyEmailBox>
    );
};

const VerifyEmailBox = styled(Box)(({ theme, device }) => {
    return {
        marginTop: theme.spacing(12),
        padding: device === 'mobile' ? 0 : theme.spacing(7),
        width: device === 'mobile' ? '100%' : 450,
        height: 'fit-content',
        background: device === 'mobile' ? 'transparent' : theme.palette.ensightful.shades[0],
        boxShadow: device === 'mobile' ? 'none' : theme.palette.ensightful.shadows[1],
        borderRadius: '20px',
    };
});

const InputStack = styled(Stack)(({ theme }) => {
    return {
        margin: theme.spacing(6, 0, 2, 0),
    };
});

export default NewPassword;
